import { useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { ResourceWorkType } from '@/api/resourceCenterApi'
import { useInvalidateResourceProfiles } from '@/hooks/useResourceCenter'

// Building blocks shared by the profile sections (z8nrz7f0vw, deck slide 9).

/**
 * The work-type colors the split bar, the capacity views and the People grid
 * all paint with, so a color always means the same thing. Checked as one
 * categorical palette, in this order, in light and dark: every adjacent pair
 * stays apart under colour-blind simulation and all sit at 3:1 or better
 * against the page. (The earlier blue/violet pair for PR/RV did not.)
 */
export const WORK_COLOR: Record<ResourceWorkType | 'FLEX', string> = {
  PROCESS: 'bg-blue-600',
  REVIEW: 'bg-teal-600',
  QA: 'bg-rose-600',
  SUPPORT: 'bg-amber-600',
  FLEX: 'bg-violet-600',
}

/** The same colors as hex, for the charts (SVG fills cannot take a class). */
export const WORK_HEX: Record<ResourceWorkType | 'FLEX', string> = {
  PROCESS: '#2563eb',
  REVIEW: '#0d9488',
  QA: '#e11d48',
  SUPPORT: '#d97706',
  FLEX: '#7c3aed',
}

/** The striped fill the deck uses for unavailable time. */
export const ABSENCE_STRIPES = 'repeating-linear-gradient(135deg, rgb(251 113 133 / 0.55) 0 3px, transparent 3px 7px)'

/** One of the six profile sections: a quiet card with a title row. */
export function SectionCard({
  icon: Icon,
  title,
  hint,
  action,
  children,
  className,
}: {
  icon: LucideIcon
  title: string
  hint?: string
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('flex flex-col rounded-2xl border bg-background p-4 shadow-sm', className)}>
      <header className='mb-3 flex items-start justify-between gap-2'>
        <div className='flex items-center gap-2.5'>
          <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary'>
            <Icon className='h-4 w-4' />
          </span>
          <div>
            <h3 className='text-sm font-semibold leading-tight'>{title}</h3>
            {hint && <p className='text-xs text-muted-foreground'>{hint}</p>}
          </div>
        </div>
        {action}
      </header>
      <div className='flex-1 space-y-3'>{children}</div>
    </section>
  )
}

/** One at-a-glance fact in the header's summary strip. */
export function StatTile({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: LucideIcon
  label: string
  value: ReactNode
  sub?: ReactNode
}) {
  return (
    <div className='flex items-center gap-2.5 rounded-xl border bg-background px-3 py-2 shadow-sm'>
      <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
        <Icon className='h-4 w-4' />
      </span>
      <div className='min-w-0 flex-1'>
        <p className='truncate text-xs font-medium text-muted-foreground'>{label}</p>
        <p className='truncate text-sm font-semibold leading-tight'>{value}</p>
        {sub && <p className='truncate text-xs text-muted-foreground'>{sub}</p>}
      </div>
    </div>
  )
}

/** A compact segmented switch (Fixed · Pattern · Variable, % · Dynamic…). */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  ariaLabel,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  disabled?: boolean
  ariaLabel: string
}) {
  return (
    <div role='radiogroup' aria-label={ariaLabel} className='inline-flex rounded-lg bg-muted p-0.5'>
      {options.map(o => (
        <button
          key={o.value}
          type='button'
          role='radio'
          aria-checked={value === o.value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed',
            value === o.value ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** A labelled field. */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn('block space-y-1', className)}>
      <span className='block text-xs font-medium text-muted-foreground'>{label}</span>
      {children}
    </label>
  )
}

export function DateInput({
  value,
  onChange,
  disabled,
  ariaLabel,
  min,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  ariaLabel?: string
  min?: string
}) {
  return (
    <Input
      type='date'
      value={value}
      min={min}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={e => onChange(e.target.value)}
      className='h-8 text-sm'
    />
  )
}

/** "Oct 12, 2026" from "2026-10-12", read in UTC so the day never shifts. */
export function longDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0]!.toUpperCase())
    .join('')
}

/**
 * Every profile write goes through here: one error line per section, and the
 * profile, the People rows and the calendar all refetch on success — the deck's
 * "every change updates the calendar immediately".
 */
export function useProfileWrite() {
  const invalidate = useInvalidateResourceProfiles()
  const [error, setError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      setError(null)
      void invalidate()
    },
    onError: e => setError(e instanceof Error ? e.message : 'Something went wrong.'),
  })
  return {
    run: (fn: () => Promise<unknown>, onDone?: () => void) => mutation.mutate(fn, { onSuccess: () => onDone?.() }),
    pending: mutation.isPending,
    error,
    clearError: () => setError(null),
  }
}

export function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null
  return <p className='rounded-md bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive'>{message}</p>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className='rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground'>{children}</p>
}

/** "From Oct 12, 2026", "Oct 12, 2026" (one day) or "Oct 12 – Oct 20, 2026". Null `to` = open-ended. */
export function dateRange(from: string, to: string | null): string {
  if (to === null) return `From ${longDate(from)}`
  return from === to ? longDate(from) : `${longDate(from)} – ${longDate(to)}`
}
